import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import {
  DropdownContainer,
  DropdownHeader,
  SuggestionsList,
  SuggestionItem,
  TyreTitle,
  TyreDetails,
  TyreBrand,
  DetailBadge,
  PriceBadge,
  Highlight,
  LoadingContainer,
  LoadingSpinner,
  EmptyState,
} from './SearchSuggestions.styled';
import { formatDimensionsCompact } from '../../../../lib/parseTyreDimensions';
import { orange } from '../../../../style/colors';
import formatCurrency from '../../../../lib/formatCurrency';

const getSeasonTranslationKey = (seasonName) => {
  const seasonMap = {
    'summer': 'summer',
    'winter': 'winter',
    'all season': 'allSeason',
    'all-season': 'allSeason',
  };
  return seasonMap[seasonName?.toLowerCase()] || seasonName;
};

const highlightText = (text, query) => {
  if (!query || !text) return text;
  
  const regex = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
  const parts = text.split(regex);
  
  return parts.map((part, index) => 
    regex.test(part) ? <Highlight key={index}>{part}</Highlight> : part
  );
};

const SearchSuggestions = ({
  isVisible,
  suggestions,
  searchQuery,
  onSelectSuggestion,
  isLoading,
}) => {
  const { t } = useTranslation();
  
  if (!isVisible) return null;

  return (
    <DropdownContainer isVisible={isVisible}>
      <DropdownHeader>
        <i className="mdi mdi-lightbulb-on" />
        {t('tyreSuggestions')}
      </DropdownHeader>
      {isLoading ? (
        <LoadingContainer>
          <LoadingSpinner className="mdi mdi-loading" />
          <div>{t('searchingBestMatches')}</div>
        </LoadingContainer>
      ) : suggestions.length > 0 ? (
        <SuggestionsList>
          {suggestions.map((tyre) => (
            <SuggestionItem
              key={tyre._id}
              onClick={() => onSelectSuggestion(tyre)}
            >
              <TyreTitle>
                <i className="mdi mdi-tire" style={{ fontSize: '20px', color: orange }} />
                {highlightText(tyre.catalogNumber, searchQuery)}
              </TyreTitle>
              <TyreDetails>
                {tyre.brand?.name && <TyreBrand>{tyre.brand.name}</TyreBrand>}
                {tyre.dimensions && <DetailBadge>{formatDimensionsCompact(tyre.dimensions) || tyre.dimensions}</DetailBadge>}
                {tyre.season?.name && <DetailBadge><i className="mdi mdi-weather-snowy-rainy" /> {t(getSeasonTranslationKey(tyre.season.name))}</DetailBadge>}
                {tyre.stock > 0 && <DetailBadge><i className="mdi mdi-package-variant" /> {tyre.stock} {t('pcs')}</DetailBadge>}
                {tyre.grossPrice && <PriceBadge>{formatCurrency(tyre.grossPrice)}</PriceBadge>}
              </TyreDetails>
            </SuggestionItem>
          ))}
        </SuggestionsList>
      ) : (
        <EmptyState>
          <i className="mdi mdi-magnify-close" />
          <div>{t('noSuggestionsFor')} "{searchQuery}"</div>
        </EmptyState>
      )}
    </DropdownContainer>
  );
};

SearchSuggestions.propTypes = {
  isVisible: PropTypes.bool.isRequired,
  suggestions: PropTypes.arrayOf(PropTypes.object).isRequired,
  searchQuery: PropTypes.string,
  onSelectSuggestion: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
};

export default SearchSuggestions;


